import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, take, tap } from 'rxjs';

import { CowDataFilters } from '../datasource/cow.datasource';
import { CowWithMeasurement } from '../models/cow.model';
import { GraphDataDto, Measurement, MeasurementRequest } from '../models/measurement.model';
import { toDateOnlyString } from '../utils/date.utils';

@Injectable({
  providedIn: 'root',
})
export class CowService {
  private readonly http = inject(HttpClient);

  private readonly env = inject(EnvironmentInjector);
  private readonly baseUrl = `${this.env.APIM_BASE_URL}/herd-v2-service`;
  private readonly availableDates: WritableSignal<Date[]> = signal<Date[]>([]);

  public getCowsForMeasurement(filters: CowDataFilters): Observable<CowWithMeasurement[]> {
    const date = toDateOnlyString(filters.measurementDate);
    return this.http.get<CowWithMeasurement[]>(
      `${this.baseUrl}/cows/measurements/${date}?allowEmptyMeasurements=${filters.allowEmptyMeasurements}`,
    );
  }

  public getCowMeasurementHistory(
    cowId: string,
    allowEmptyMeasurements = false,
  ): Observable<CowWithMeasurement[]> {
    return this.http.get<CowWithMeasurement[]>(
      `${this.baseUrl}/cows/measurements/cow/${cowId}?allowEmptyMeasurements=${allowEmptyMeasurements}`,
    );
  }

  public batchSaveCowMeasurements(
    measurements: Measurement[],
    measurementDate: Date,
    measurementCowIdsToDelete: string[] = [],
  ): Observable<HttpResponse<object>> {
    const request: MeasurementRequest = {
      measurementDate: toDateOnlyString(measurementDate),
      measurements,
      measurementCowIdsToDelete,
    };
    return this.http.post<object>(`${this.baseUrl}/cows/measurements/changes`, request, {
      observe: 'response',
    });
  }

  public getMeasurementDates(): Observable<Date[]> {
    return this.http.get<Date[]>(`${this.baseUrl}/cows/measurements/dates`);
  }

  public getChartData(dates: (Date | string)[]): Observable<GraphDataDto> {
    const dateParams = dates.map((date) => toDateOnlyString(date)).join('&dates=');
    return this.http.get<GraphDataDto>(
      `${this.baseUrl}/cows/measurements/graph-data?dates=${dateParams}`,
    );
  }

  public excludeCowFromMeasurements(
    registrationNumber: string,
    exclude: boolean,
  ): Observable<HttpResponse<object>> {
    return this.http.post(
      `${this.baseUrl}/cows/measurements/exclude`,
      {
        registrationNumber: registrationNumber,
        excludeFromMeasurements: exclude,
      },
      { observe: 'response' },
    );
  }

  public getAvailableDates(): Signal<Date[]> {
    return this.availableDates.asReadonly();
  }

  public refreshMeasurementDates(): void {
    this.loadMeasurementDates().pipe(take(1)).subscribe();
  }

  public loadMeasurementDates(): Observable<Date[]> {
    return this.getMeasurementDates().pipe(
      tap((dates) => {
        this.availableDates.set(dates.map((date) => new Date(date)));
      }),
    );
  }
}
